<template>
    <bank-cards-find
        :params="{
            card: $route.params.card
        }"
        v-slot="{
            data: card,
            has_loaded,
            error: find_error,
            find
        }"
        :find-on-created="true"
    >
        <div>
            <template v-if="!has_loaded || find_error">
                <base-loading
                    :error="find_error"
                    :retry="find"
                    class="py-6"
                />

                <error-modal
                    v-if="find_error"
                    :error="find_error"
                />
            </template>

            <template v-else>
                <base-alert
                    v-if="card.is_verified"
                    level="warning"
                    class="mb-6"
                >
                    <template slot="heading">Card Already Confirmed</template>

                    <template slot="content">
                        <p>This card was confirmed on {{ card.verified_at | moment('D MMM, YYYY') }}.</p>
                    </template>
                </base-alert>

                <div class="mb-6 bg-white rounded shadow">
                    <bank-cards-verify
                        :params="{
                            card: card.uuid
                        }"
                        v-slot="{
                            sid,
                            execute,
                            error: verify_error,
                            is_loading: is_saving
                        }"
                        @success="onSuccess"
                        @error="onError"
                    >
                        <base-form @submit="execute(form)">
                            <header class="flex items-center px-4 py-3 border-b sm:px-6 md:py-4">
                                <h1 class="text-lg font-normal">
                                    Confirm
                                    <span class="uppercase">{{ card.brand }}</span>
                                    ending {{ card.last_four }}
                                </h1>
                            </header>

                            <div class="px-6 pt-6 border-b">
                                <validator-alert :sid="sid" />

                                <div class="text-grey-darker">
                                    <p class="mb-3">
                                        We made a £1 charge on your bank card. The charge created a <strong>6-character code</strong> that can be
                                        found on <strong>your statement</strong>.
                                    </p>

                                    <p class="mb-3">
                                        To begin using your card with Rentora, please enter the 6-character code we sent to your
                                        bank card below.
                                    </p>

                                    <p class="mb-3">Once your card is confirmed, we will deduct any charges from your first payment(s) as a refund.</p>

                                    <p class="mb-6">Please note: The charge should appear on your account within a few minutes of adding your card, but may take up to two hours, depending on your bank.</p>

                                    <h6 class="mb-3 text-grey-darkest">Sample Statement</h6>

                                    <div class="overflow-y-auto scrollbar-hidden">
                                        <table-ish class="mb-3 border rounded">
                                            <table-ish-row-header class="rounded-t">
                                                <table-ish-cell :shrink="true">
                                                    Date
                                                </table-ish-cell>

                                                <table-ish-cell>
                                                    Description
                                                </table-ish-cell>

                                                <table-ish-cell :shrink="true">
                                                    Amount
                                                </table-ish-cell>
                                            </table-ish-row-header>

                                            <table-ish-row>
                                                <table-ish-cell :shrink="true">
                                                    {{ $moment.now() | moment('DD/MM/YYYY') }}
                                                </table-ish-cell>

                                                <table-ish-cell>
                                                    RENTORA*
                                                    <span class="underline"><!--
                                                        -->AB1234<!--
                                                    --></span>
                                                </table-ish-cell>

                                                <table-ish-cell :shrink="true">
                                                    £1.00
                                                </table-ish-cell>
                                            </table-ish-row>
                                        </table-ish>
                                    </div>

                                    <p class="mb-6">
                                        Sample Rentora code:
                                        <code class="px-2 pt-1 leading-none rounded pb-0-5 bg-grey-bg"><!--
                                            -->AB1234<!--
                                        --></code>
                                    </p>
                                </div>
                            </div>

                            <div class="px-6 pt-6 border-b">
                                <form-section>
                                    <template slot="title">Code</template>
                                    <template slot="description">
                                        The code is always <strong>two letters</strong> followed by <strong>four numbers</strong>.
                                    </template>

                                    <grid-layout>
                                        <grid-layout-item class="md:w-1/2">
                                            <form-item
                                                :sid="sid"
                                                :is-required="true"
                                                name="token"
                                            >
                                                <template slot="label">code</template>

                                                <input-text
                                                    v-model="form.token"
                                                    :sid="sid"
                                                    :disabled="is_saving || card.is_verified"
                                                    placeholder="6-character code"
                                                    name="token"
                                                />
                                            </form-item>
                                        </grid-layout-item>
                                    </grid-layout>
                                </form-section>
                            </div>

                            <base-form-footer class="rounded-b">
                                <base-button
                                    :is-loading="is_saving"
                                    class="w-full btn-blue md:w-auto"
                                    :disabled="card.is_verified"
                                >
                                    Confirm Bank Card
                                </base-button>
                            </base-form-footer>

                            <modal-success :is-open="show_updated_modal">
                                <template slot="heading">Bank card confirmed!</template>

                                <p class="mb-3">
                                    Thank you for confirming your bank card. You can now use this card to make payments
                                    on Rentora.
                                </p>

                                <template slot="footer">
                                    <div class="flex flex-col md:flex-row-reverse">
                                        <router-link
                                            v-if="card"
                                            :to="{
                                                name: $router.fix('dashboard.*.bank-cards.show'),
                                                params: {
                                                    card: card.uuid
                                                }
                                            }"
                                            class="text-center btn btn-green"
                                        >
                                            Go to Bank Card
                                        </router-link>
                                        <router-link
                                            :to="{ name: $router.fix('dashboard.*.settings.index') }"
                                            class="mt-3 text-center btn md:mt-0 md:mr-3"
                                        >
                                            Go to Settings
                                        </router-link>
                                    </div>
                                </template>
                            </modal-success>

                            <error-modal
                                v-if="verify_error && $error.is(verify_error, { exclude: [400, 422] })"
                                :error="verify_error"
                            />
                        </base-form>
                    </bank-cards-verify>
                </div>
            </template>
        </div>
    </bank-cards-find>
</template>

<script>
    import scroll from '../../../../../support/scroll'
    import BankCardsFind from '../../../../../components/bank-cards-find'
    import BankCardsVerify from '../../../../../components/bank-cards-verify'

    export default {
        components: {
            BankCardsFind,
            BankCardsVerify
        },
        data () {
            return {
                show_updated_modal: false,
                form: {
                    token: ''
                }
            }
        },
        methods: {
            onSuccess () {
                setTimeout(() => {
                    this.show_updated_modal = true
                }, 0)
            },
            onError () {
                scroll.toTop()
            }
        }
    }
</script>
